import {
  BarChart3,
  Eye,
  Heart,
  MessageCircle,
  MousePointerClick,
  Percent,
  Share2,
  TrendingUp,
  Users,
  UserPlus,
  Video,
  type LucideIcon,
} from "lucide-react";
import type {
  OrganicSocialPlatform,
  SocialContentRow,
  SocialDailyPoint,
  SocialTotals,
  SocialTypePerformance,
} from "../../../data/organicSocialMockData";

export type SocialValueFormat = "count" | "signed" | "percent";

export interface SocialKpiDefinition {
  id: keyof SocialTotals;
  labelKey: string;
  icon: LucideIcon;
  format: SocialValueFormat;
  /** Leaf used by `getOrganicPlatformMetricDefinitionInfo` for card help. */
  helpLeaf: string;
}

export const SOCIAL_KPI_DEFINITIONS: SocialKpiDefinition[] = [
  { id: "followers", labelKey: "organicMedia.social.kpis.followers", icon: Users, format: "count", helpLeaf: "newFollowers" },
  {
    id: "followerGrowth",
    labelKey: "organicMedia.social.kpis.followerGrowth",
    icon: UserPlus,
    format: "signed",
    helpLeaf: "newFollowers",
  },
  {
    id: "contentCreated",
    labelKey: "organicMedia.social.kpis.contentCreated",
    icon: Video,
    format: "count",
    helpLeaf: "postsCreated",
  },
  { id: "views", labelKey: "organicMedia.social.kpis.views", icon: Eye, format: "count", helpLeaf: "impressions" },
  {
    id: "impressions",
    labelKey: "organicMedia.social.kpis.impressions",
    icon: BarChart3,
    format: "count",
    helpLeaf: "impressions",
  },
  {
    id: "engagementRate",
    labelKey: "organicMedia.social.kpis.engagementRate",
    icon: TrendingUp,
    format: "percent",
    helpLeaf: "engagementRate",
  },
  { id: "ctr", labelKey: "organicMedia.social.kpis.ctr", icon: Percent, format: "percent", helpLeaf: "ctr" },
  {
    id: "linkClicks",
    labelKey: "organicMedia.social.kpis.linkClicks",
    icon: MousePointerClick,
    format: "count",
    helpLeaf: "linkClicks",
  },
  { id: "shares", labelKey: "organicMedia.social.kpis.shares", icon: Share2, format: "count", helpLeaf: "shares" },
  {
    id: "comments",
    labelKey: "organicMedia.social.kpis.comments",
    icon: MessageCircle,
    format: "count",
    helpLeaf: "comments",
  },
  { id: "likes", labelKey: "organicMedia.social.kpis.likes", icon: Heart, format: "count", helpLeaf: "likes" },
];

export type SocialTypeMetric = keyof Pick<
  SocialTypePerformance,
  "engagementRate" | "views" | "impressions" | "likes" | "comments" | "shares" | "linkClicks" | "ctr"
>;

export const SOCIAL_TYPE_METRIC_OPTIONS: Array<{
  value: SocialTypeMetric;
  labelKey: string;
  format: SocialValueFormat;
}> = [
  {
    value: "engagementRate",
    labelKey: "organicMedia.social.kpis.engagementRate",
    format: "percent",
  },
  { value: "views", labelKey: "organicMedia.social.kpis.views", format: "count" },
  { value: "impressions", labelKey: "organicMedia.social.kpis.impressions", format: "count" },
  { value: "likes", labelKey: "organicMedia.social.kpis.likes", format: "count" },
  { value: "comments", labelKey: "organicMedia.social.kpis.comments", format: "count" },
  { value: "shares", labelKey: "organicMedia.social.kpis.shares", format: "count" },
  { value: "linkClicks", labelKey: "organicMedia.social.kpis.linkClicks", format: "count" },
  { value: "ctr", labelKey: "organicMedia.social.kpis.ctr", format: "percent" },
];

export type SocialTrendMetric = keyof Pick<SocialDailyPoint, "engagementRate" | "views">;

export const SOCIAL_TREND_SERIES: Array<{
  key: SocialTrendMetric;
  labelKey: string;
  format: SocialValueFormat;
}> = [
  {
    key: "engagementRate",
    labelKey: "organicMedia.social.kpis.engagementRate",
    format: "percent",
  },
  { key: "views", labelKey: "organicMedia.social.kpis.views", format: "count" },
];

export type SocialContentSortKey = keyof Pick<
  SocialContentRow,
  | "caption"
  | "impressions"
  | "views"
  | "linkClicks"
  | "likes"
  | "comments"
  | "shares"
  | "engagementRate"
>;

export const SOCIAL_CONTENT_COLUMNS: Array<{
  key: SocialContentSortKey;
  labelKey: string;
  format: SocialValueFormat;
  /** Leaf used by `getOrganicPlatformMetricDefinitionInfo` for column help. */
  helpLeaf: string;
}> = [
  {
    key: "impressions",
    labelKey: "organicMedia.social.kpis.impressions",
    format: "count",
    helpLeaf: "impressions",
  },
  {
    key: "views",
    labelKey: "organicMedia.social.kpis.views",
    format: "count",
    helpLeaf: "impressions",
  },
  {
    key: "linkClicks",
    labelKey: "organicMedia.social.kpis.linkClicks",
    format: "count",
    helpLeaf: "linkClicks",
  },
  {
    key: "likes",
    labelKey: "organicMedia.social.kpis.likes",
    format: "count",
    helpLeaf: "likes",
  },
  {
    key: "comments",
    labelKey: "organicMedia.social.kpis.comments",
    format: "count",
    helpLeaf: "comments",
  },
  {
    key: "shares",
    labelKey: "organicMedia.social.kpis.shares",
    format: "count",
    helpLeaf: "shares",
  },
  {
    key: "engagementRate",
    labelKey: "organicMedia.social.kpis.engagementRate",
    format: "percent",
    helpLeaf: "engagementRate",
  },
];

export const SOCIAL_CONTENT_PAGE_SIZE = 8;

export const SOCIAL_PLATFORM_LABEL_KEY: Record<OrganicSocialPlatform, string> = {
  tiktok: "organicMedia.platform.tiktok",
  snapchat: "organicMedia.platform.snapchat",
};

export const SOCIAL_PLATFORM_TAB_KEY: Record<OrganicSocialPlatform, string> = {
  tiktok: "organicMedia.tabs.tiktokContentFollowers",
  snapchat: "organicMedia.tabs.snapchatContentFollowers",
};
